import { JenkinsSettingsScreen } from '../features/company/JenkinsSettingsScreen';
import { KubernetesSettingsScreen } from '../features/kubernetes/KubernetesSettingsScreen';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { AppShell } from './AppShell';
import { CompanyLayout } from './CompanyLayout';
import { ForgotPasswordScreen } from '../features/auth/ForgotPasswordScreen';
import { LoginScreen } from '../features/auth/LoginScreen';
import { RegisterScreen } from '../features/auth/RegisterScreen';
import { ResetPasswordScreen } from '../features/auth/ResetPasswordScreen';
import { VerifyEmailScreen } from '../features/auth/VerifyEmailScreen';
import { AuditScreen } from '../features/company/AuditScreen';
import { CompanyPanel } from '../features/company/CompanyPanel';
import { CompanyWizard } from '../features/company/CompanyWizard';
import { CredentialsScreen } from '../features/company/CredentialsScreen';
import { DangerZoneScreen } from '../features/company/DangerZoneScreen';
import { GeneralSettingsScreen } from '../features/company/GeneralSettingsScreen';
import { GithubSettingsScreen } from '../features/company/GithubSettingsScreen';
import { GlobalPanel } from '../features/company/GlobalPanel';
import { ReposSettingsScreen } from '../features/company/ReposSettingsScreen';
import { ApprovalsScreen } from '../features/approvals/ApprovalsScreen';
import { CompanyApprovalsScreen } from '../features/approvals/CompanyApprovalsScreen';
import { ConversationsScreen } from '../features/communication/ConversationsScreen';
import { AgreementsScreen } from '../features/agreements/AgreementsScreen';
import { EmployeesScreen } from '../features/employee/EmployeesScreen';
import { TemplatesScreen } from '../features/employee/TemplatesScreen';
import { DepartmentsScreen } from '../features/org/DepartmentsScreen';
import { ProjectsScreen } from '../features/project/ProjectsScreen';
import { NewTaskScreen } from '../features/tasks/NewTaskScreen';
import { TasksScreen } from '../features/tasks/TasksScreen';
import { TeamsScreen } from '../features/org/TeamsScreen';
import { AccountScreen } from '../features/settings/AccountScreen';
import { NotificationsScreen } from '../features/notifications/NotificationsScreen';
import { REGISTRATION_OPEN } from '../lib/registration';
import { useAuth } from '../stores/auth';

/**
 * Code-based rather than file-based routing.
 *
 * The trade is a plugin and a generated, committed `routeTree.gen.ts` against
 * one file that shows the whole map at a glance, and with ~20 routes that is
 * still the better half of the trade.
 *
 * Phase 2 adopts the deferred half: React Flow and dagre are the first
 * dependencies heavy enough to matter against the bundle budget of `13 §1`, and
 * they are needed by exactly one screen. The org chart, the employee wizard, the
 * employee detail and the permissions editor are therefore `lazyRouteComponent`,
 * which the map above still shows in one place — the split is a property of four
 * lines, not of the file layout.
 */
const rootRoute = createRootRoute({ component: Outlet });

/** Public: reachable with no session at all. */
const publicRoutes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginScreen }),
  // El alta pública está cerrada salvo que el operador la abra: la plataforma
  // es privada. La ruta sigue existiendo cuando está cerrada, pero redirige a
  // /login en lugar de pintar el formulario — un enlace viejo o un marcador
  // llevan a donde se puede hacer algo, no a un 404 ni a un formulario que el
  // backend va a rechazar con 403.
  REGISTRATION_OPEN
    ? createRoute({ getParentRoute: () => rootRoute, path: '/register', component: RegisterScreen })
    : createRoute({
        getParentRoute: () => rootRoute,
        path: '/register',
        beforeLoad: () => {
          throw redirect({ to: '/login' });
        },
      }),
  createRoute({ getParentRoute: () => rootRoute, path: '/verify-email', component: VerifyEmailScreen }),
  createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPasswordScreen }),
  createRoute({ getParentRoute: () => rootRoute, path: '/reset-password', component: ResetPasswordScreen }),
];

/**
 * Everything below here needs a session.
 *
 * The guard waits for `ready` before deciding. Without that wait, a hard reload
 * would redirect to `/login` in the instant before the boot refresh finishes —
 * the flash of "signed out" that this design otherwise invites, since the access
 * token lives only in memory.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: AppShell,
  beforeLoad: ({ location }) => {
    const { ready, accessToken } = useAuth.getState();

    if (ready && !accessToken) {
      throw redirect({ to: '/login', search: { next: location.href } });
    }
  },
});

const globalPanelRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: GlobalPanel,
});

const accountRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: AccountScreen,
});

/** The approvals inbox spans every company of the caller, so it lives outside the company scope (`13 §2`). */
const approvalsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/approvals',
  component: ApprovalsScreen,
});

/** Same reasoning as approvals: the notification inbox is personal, not a company's (`13 §2`). */
const notificationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/notifications',
  component: NotificationsScreen,
});

const newCompanyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/companies/new',
  component: CompanyWizard,
});

/** The company scope of `13 §2`, addressed by slug so a URL is shareable. */
const companyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/c/$companySlug',
  component: CompanyLayout,
});

/**
 * The four screens that are code-split. Everything else is small enough that a
 * second request would cost more than the bytes it saves.
 */
const OrgChartScreen = lazyRouteComponent(() => import('../features/org/OrgChartScreen'), 'OrgChartScreen');
// La bandeja de feedback de la empresa: rara de abrir a diario, y arrastra el revisor entero.
const CompanyFeedbackScreen = lazyRouteComponent(() => import('../features/feedback/CompanyFeedbackScreen'), 'CompanyFeedbackScreen');
/**
 * The org chart's assistant is split for the reason the template creator is: it
 * pulls in the conversation panel, the plan editor and the findings list, and
 * it is a screen somebody opens once while a company is being set up.
 */
const OrgAssistScreen = lazyRouteComponent(() => import('../features/org/OrgAssistScreen'), 'OrgAssistScreen');
/** El que contesta al verificador se parte por lo mismo, y además solo se abre
 *  cuando el panel del organigrama tiene algo de lo que quejarse. */
const ReadinessAssistScreen = lazyRouteComponent(
  () => import('../features/org/ReadinessAssistScreen'),
  'ReadinessAssistScreen',
);
/** El de personal se parte por lo mismo: arrastra el panel de conversación, el
 *  editor del plan y la lista de hallazgos, y se abre una vez por proyecto. */
const StaffingAssistScreen = lazyRouteComponent(
  () => import('../features/project/StaffingAssistScreen'),
  'StaffingAssistScreen',
);
const EmployeeWizard = lazyRouteComponent(() => import('../features/employee/EmployeeWizard'), 'EmployeeWizard');
const TemplateCreatorScreen = lazyRouteComponent(
  () => import('../features/employee/TemplateCreatorScreen'),
  'TemplateCreatorScreen',
);
const EmployeeDetailScreen = lazyRouteComponent(
  () => import('../features/employee/EmployeeDetailScreen'),
  'EmployeeDetailScreen',
);
/**
 * The permissions editor is the fourth: the matrix renders N×N cells and pulls
 * in the policy editor and the simulator with it, and it is reached from one tab
 * of one company.
 */
const PermissionsScreen = lazyRouteComponent(
  () => import('../features/permissions/PermissionsScreen'),
  'PermissionsScreen',
);
/**
 * The fifth, and split for a different reason than the other four: it is not
 * heavy, it is *rarely open*. A live trace is a screen a manager visits while
 * something is running and never again, so its bytes should not be in the
 * bundle every other screen pays for.
 */
const RunsScreen = lazyRouteComponent(() => import('../features/runs/RunsScreen'), 'RunsScreen');
/**
 * La consola del clúster se parte por lo que se parte la traza en vivo: es una
 * pantalla que se abre mientras algo va mal y arrastra la consola entera
 * —listas por tipo, describe, logs— que ninguna otra pantalla necesita.
 */
const ClusterScreen = lazyRouteComponent(() => import('../features/kubernetes/ClusterScreen'), 'ClusterScreen');
/**
 * The sixth, and split for the same reason as the run viewer rather than for
 * weight: one project at a time is what a manager opens, and the screen pulls in
 * the settings editor, the conventions editor and the staffing panel with it —
 * three editors that the projects *list* has no use for.
 */
const ProjectDetailScreen = lazyRouteComponent(
  () => import('../features/project/ProjectDetailScreen'),
  'ProjectDetailScreen',
);
/**
 * The task detail carries the run viewer and the timeline with it — the runs
 * split's reasoning applies verbatim. The autonomy screen is the permissions
 * editor's case again: a form-heavy editor reached from one settings tab.
 */
const TaskDetailScreen = lazyRouteComponent(
  () => import('../features/tasks/TaskDetailScreen'),
  'TaskDetailScreen',
);
const AutonomyScreen = lazyRouteComponent(() => import('../features/autonomy/AutonomyScreen'), 'AutonomyScreen');
/**
 * The three phase-9 screens are split like the autonomy editor: each is a
 * form- or chart-heavy page reached from one tab of one company, and none of
 * their bytes belong in the bundle every other screen pays for.
 */
const CostsScreen = lazyRouteComponent(() => import('../features/costs/CostsScreen'), 'CostsScreen');
const BudgetsScreen = lazyRouteComponent(() => import('../features/costs/BudgetsScreen'), 'BudgetsScreen');
const WebhooksScreen = lazyRouteComponent(() => import('../features/webhooks/WebhooksScreen'), 'WebhooksScreen');

const companyRoutes = [
  createRoute({ getParentRoute: () => companyRoute, path: '/', component: CompanyPanel }),
  createRoute({ getParentRoute: () => companyRoute, path: '/org', component: OrgChartScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/org/assistant', component: OrgAssistScreen }),
  createRoute({
    // `/repairs` y no `/org/repairs`: el ayudante contesta a los dos
    // verificadores desde que aprendió el de puesta a punto, y se llega a él
    // tanto desde el organigrama como desde el panel de empresa. Una ruta
    // colgada de `org` diría que es cosa del organigrama.
    getParentRoute: () => companyRoute,
    path: '/repairs',
    component: ReadinessAssistScreen,
    // `?check=` siembra la conversación con una sola queja: quien llega desde la
    // fila del verificador quiere hablar de esa, no del informe entero.
    validateSearch: (search: Record<string, unknown>): { check?: string } =>
      typeof search.check === 'string' && search.check !== '' ? { check: search.check } : {},
  }),
  createRoute({ getParentRoute: () => companyRoute, path: '/departments', component: DepartmentsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/teams', component: TeamsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/employees', component: EmployeesScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/employees/new', component: EmployeeWizard }),
  // Before `$employeeSlug`, which would otherwise swallow it: TanStack ranks
  // static segments above dynamic ones, but the order here is the one a reader
  // checks first.
  createRoute({ getParentRoute: () => companyRoute, path: '/employees/templates', component: TemplatesScreen }),
  createRoute({
    getParentRoute: () => companyRoute,
    path: '/employees/templates/new',
    component: TemplateCreatorScreen,
  }),
  createRoute({
    getParentRoute: () => companyRoute,
    path: '/employees/$employeeSlug',
    component: EmployeeDetailScreen,
  }),
  createRoute({ getParentRoute: () => companyRoute, path: '/permissions', component: PermissionsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/runs', component: RunsScreen }),
  createRoute({
    getParentRoute: () => companyRoute,
    path: '/tasks',
    component: TasksScreen,
    // La pestaña de proyecto y la búsqueda van en la URL: un enlace a un
    // tablero concreto tiene que abrir ese tablero, y «atrás» desde una tarea
    // tiene que devolver a la pestaña —y a la búsqueda— de la que se salió.
    validateSearch: (search: Record<string, unknown>): { project?: string; q?: string } => ({
      ...(typeof search.project === 'string' && search.project !== '' ? { project: search.project } : {}),
      ...(typeof search.q === 'string' && search.q !== '' ? { q: search.q } : {}),
    }),
  }),
  createRoute({
    getParentRoute: () => companyRoute,
    path: '/tasks/new',
    component: NewTaskScreen,
    // `?project=` preselecciona el proyecto: quien pulsa «Nueva tarea» desde
    // el tablero de Web Shop quiere una tarea de Web Shop.
    // `?type=ops&assignee=<id>&queue=1` es el preset de «Preguntar a un
    // empleado» (`22 §7`): una tarea `ops` ya encolada para quien se elige.
    validateSearch: (search: Record<string, unknown>): { project?: string; type?: 'ops'; assignee?: string; queue?: '1' } => ({
      ...(typeof search.project === 'string' && search.project !== '' ? { project: search.project } : {}),
      ...(search.type === 'ops' ? { type: 'ops' as const } : {}),
      ...(typeof search.assignee === 'string' && search.assignee !== '' ? { assignee: search.assignee } : {}),
      ...(search.queue === '1' || search.queue === 1 || search.queue === true ? { queue: '1' as const } : {}),
    }),
  }),
  createRoute({ getParentRoute: () => companyRoute, path: '/tasks/$taskNumber', component: TaskDetailScreen }),
  // El feedback pendiente de todas las apps de la empresa (`20 · Interfaz y operación`).
  createRoute({ getParentRoute: () => companyRoute, path: '/feedback', component: CompanyFeedbackScreen }),
  // Las compuertas de esta empresa; la bandeja personal de todas sigue en `/approvals`.
  createRoute({ getParentRoute: () => companyRoute, path: '/approvals', component: CompanyApprovalsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/autonomy', component: AutonomyScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/projects', component: ProjectsScreen }),
  createRoute({
    getParentRoute: () => companyRoute,
    path: '/projects/$projectSlug',
    component: ProjectDetailScreen,
    // La pestaña va en la URL y no solo en el estado del componente, para que
    // volver de dotar el proyecto aterrice en Personal y no en Resumen.
    validateSearch: (search: Record<string, unknown>): { tab?: 'overview' | 'repositories' | 'members' | 'feedback' | 'jenkins' | 'kubernetes' } =>
      search.tab === 'overview' || search.tab === 'repositories' || search.tab === 'members' || search.tab === 'feedback' || search.tab === 'jenkins' || search.tab === 'kubernetes'
        ? { tab: search.tab }
        : {},
  }),
  createRoute({
    getParentRoute: () => companyRoute,
    path: '/projects/$projectSlug/staffing',
    component: StaffingAssistScreen,
  }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/general', component: GeneralSettingsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/credentials', component: CredentialsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/repos', component: ReposSettingsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/github', component: GithubSettingsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/jenkins', component: JenkinsSettingsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/kubernetes', component: KubernetesSettingsScreen }),
  createRoute({
    getParentRoute: () => companyRoute,
    path: '/cluster',
    component: ClusterScreen,
    // Contexto, namespace y tipo van en la URL para que un enlace abra
    // exactamente la vista que alguien quiere enseñar.
    validateSearch: (search: Record<string, unknown>): { context?: string; namespace?: string; kind?: string } => ({
      ...(typeof search.context === 'string' && search.context !== '' ? { context: search.context } : {}),
      ...(typeof search.namespace === 'string' && search.namespace !== '' ? { namespace: search.namespace } : {}),
      ...(typeof search.kind === 'string' && search.kind !== '' ? { kind: search.kind } : {}),
    }),
  }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/danger', component: DangerZoneScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/budgets', component: BudgetsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/settings/notifications', component: WebhooksScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/costs', component: CostsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/conversations', component: ConversationsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/agreements', component: AgreementsScreen }),
  createRoute({ getParentRoute: () => companyRoute, path: '/audit', component: AuditScreen }),
];

const routeTree = rootRoute.addChildren([
  ...publicRoutes,
  authenticatedRoute.addChildren([
    globalPanelRoute,
    accountRoute,
    approvalsRoute,
    notificationsRoute,
    newCompanyRoute,
    companyRoute.addChildren(companyRoutes),
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
